import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useShell } from '../../context/ShellContext';
import SideSheet from './SideSheet';
import './ContentPane.css';

function getChatRoute(chat) {
  if (!chat) return '/friends';
  return chat.parent_chat_id
    ? `/channel/${chat.parent_chat_id}/${chat.id}`
    : `/channel/${chat.id}`;
}

function getChatTabLabel(chat, parentChat) {
  if (!chat) return '';
  if (!chat.parent_chat_id) return chat.name;
  return `${chat.name} | ${parentChat?.name || 'Parent Chat'}`;
}

export default function ContentPane({
  selectedChat,
  selectedChatAccessRoot,
  selectedChatChildren,
  unreadChatIds,
  canCreateChildChats,
  onOpenCreateChildModal,
  children,
}) {
  const navigate = useNavigate();
  const { isNavPinned, subChatsPaneOpen, closeSubChatsPane } = useShell();
  const isMobile = !isNavPinned;

  const allTabs = [selectedChatAccessRoot, ...selectedChatChildren];

  function navigateToTab(tab) {
    navigate(getChatRoute(tab));
    closeSubChatsPane();
  }

  return (
    <div className="room-frame">
      {!isMobile && (
        <div className="room-tabs-bar">
          <div className="room-tabs-scroller" role="tablist" aria-label="Chat tabs">
            <button
              className={`room-tab${selectedChat.id === selectedChatAccessRoot.id ? ' active' : ''}`}
              type="button"
              role="tab"
              aria-selected={selectedChat.id === selectedChatAccessRoot.id}
              onClick={() => navigate(getChatRoute(selectedChatAccessRoot))}
            >
              <span className="room-tab-label">{getChatTabLabel(selectedChatAccessRoot, selectedChatAccessRoot)}</span>
              {unreadChatIds.has(selectedChatAccessRoot.id) && <span className="room-tab-dot" aria-hidden="true" />}
            </button>
            {selectedChatChildren.map((childChat) => (
              <button
                key={childChat.id}
                className={`room-tab${selectedChat.id === childChat.id ? ' active' : ''}`}
                type="button"
                role="tab"
                aria-selected={selectedChat.id === childChat.id}
                onClick={() => navigate(getChatRoute(childChat))}
              >
                <span className="room-tab-label">{getChatTabLabel(childChat, selectedChatAccessRoot)}</span>
                {unreadChatIds.has(childChat.id) && <span className="room-tab-dot" aria-hidden="true" />}
              </button>
            ))}
          </div>

          {canCreateChildChats && (
            <button
              className="room-tab-add"
              type="button"
              onClick={onOpenCreateChildModal}
              aria-label="Create sub-chat"
              title="Create sub-chat"
            >
              +
            </button>
          )}
        </div>
      )}

      {isMobile && (
        <SideSheet open={subChatsPaneOpen} onClose={closeSubChatsPane} side="right" label="Sub-chats">
          <div className="subchat-sheet">
            <div className="subchat-sheet-header">
              <span className="subchat-sheet-title">Channels</span>
              {canCreateChildChats && (
                <button
                  className="subchat-sheet-add"
                  type="button"
                  onClick={() => { closeSubChatsPane(); onOpenCreateChildModal(); }}
                  aria-label="Create sub-chat"
                  title="Create sub-chat"
                >
                  +
                </button>
              )}
            </div>
            <nav className="subchat-sheet-list">
              {allTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`subchat-sheet-item${selectedChat.id === tab.id ? ' active' : ''}`}
                  type="button"
                  onClick={() => navigateToTab(tab)}
                >
                  <span className="subchat-sheet-item-label">{tab.name}</span>
                  {unreadChatIds.has(tab.id) && <span className="subchat-sheet-unread" aria-hidden="true" />}
                </button>
              ))}
            </nav>
          </div>
        </SideSheet>
      )}

      {children}
    </div>
  );
}
